import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import { SEGMENTATION_KEYS } from 'lumen/models/parity/insight/segmentation-item';
import ApplicationSerializer from 'lumen/serializers/application';
import { v4 as uuidv4 } from 'uuid';

type BreakdownValuePayload = {
  benchmark?: Record<string, number>;
  id: string;
  meta?: Record<string, string>;
  parent_id?: string;
  segmentation_id: string;
  segmentation_value: string;
  segmentation_value_id: string;
  segmentation_value_type: string;
  unfiltered_id?: string;
  value: Record<string, number>;
};

type BreakdownsRequestPayload = Record<
  string,
  Record<string, BreakdownValuePayload[]>
>;

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class ParityInsightSegmentationDataSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    [SEGMENTATION_KEYS.ALL]: { embedded: 'always' },
    [SEGMENTATION_KEYS.BRAND]: { embedded: 'always' },
    [SEGMENTATION_KEYS.CHANNEL]: { embedded: 'always' },
    [SEGMENTATION_KEYS.CHANNEL_TYPE]: { embedded: 'always' },
    [SEGMENTATION_KEYS.ISSUE_TYPE]: { embedded: 'always' },
    [SEGMENTATION_KEYS.MARKET]: { embedded: 'always' },
    [SEGMENTATION_KEYS.PLATFORM]: { embedded: 'always' },
    [SEGMENTATION_KEYS.SOURCE]: { embedded: 'always' },
    [SEGMENTATION_KEYS.SUBSCRIPTION]: { embedded: 'always' },
    [SEGMENTATION_KEYS.VARIANCE]: { embedded: 'always' },
  };

  extractId(): string {
    return uuidv4();
  }

  modelNameFromPayloadKey(): string {
    // Required to avoid singularization to segmentation-datum in queryRecord
    return 'parity/insight/segmentation-data';
  }

  normalizeQueryRecordResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: BreakdownsRequestPayload,
    id: string | number,
    requestType: string,
  ): ReturnType<ApplicationSerializer['normalizeQueryRecordResponse']> {
    // Flatten each breakdown key: { all: { latest: [...] } } → { all: [...] } and inject compound id using extract_date
    const flattened: Record<string, BreakdownValuePayload[]> = {};
    Object.entries(payload)
      .filter(([, periods]) => periods != null)
      .forEach(([segmentation, periods]) => {
        const [date, rows] = Object.entries(periods)[0] ?? ['latest', []];
        flattened[segmentation] = rows.map((row) => ({
          ...row,
          id: `${row.id}__${date}`,
          extract_date: date,
        }));
      });

    const restPayload = { [primaryModelClass.modelName]: flattened };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.normalizeQueryRecordResponse(
      store,
      primaryModelClass,
      restPayload,
      id,
      requestType,
    );
  }
}
